import { useMemo } from 'react';
import { Task, Level } from '@/student/types';

export interface PacingData {
  startDate: Date;
  targetDate: Date;
  targetWeeks: number;
  currentTaskIndex: number;
  totalTasks: number;
  isOnTrack: boolean;
  daysAhead: number;
}

/**
 * Hook to calculate pacing data for the journey timeline.
 */
export const usePacingData = (tasks: Task[], targetWeeks: number | null): PacingData | null => useMemo(() => {
  if (targetWeeks === null) return null;

  const now = new Date();

  // Find actual start date from first submission
  const startDates = tasks
    .filter((t) => t.submission?.createdAt)
    .map((t) => new Date(t.submission!.createdAt!).getTime());

  const startDate = startDates.length > 0 ? new Date(Math.min(...startDates)) : now;

  const targetDate = new Date(startDate);
  targetDate.setDate(targetDate.getDate() + targetWeeks * 7);

  const completedCount = tasks.filter((t) => t.submission?.state === 'marked_as_done').length;
  const totalTasks = tasks.length;

  const totalDays = targetWeeks * 7;
  const daysElapsed = (now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24);
  const expectedProgress = Math.min(1, Math.max(0, daysElapsed / totalDays));
  const actualProgress = totalTasks > 0 ? completedCount / totalTasks : 0;

  const isOnTrack = actualProgress >= expectedProgress - 0.05;
  const daysAhead = Math.round((actualProgress - expectedProgress) * totalDays);

  return {
    startDate,
    targetDate,
    targetWeeks,
    currentTaskIndex: completedCount,
    totalTasks,
    isOnTrack,
    daysAhead,
  };
}, [tasks, targetWeeks]);

const getTaskWeight = (task: Task) => {
  // Try to find a wordcount in formalCriteria
  const wordcount = task.evaluation?.formalCriteria?.wordcount;
  if (typeof wordcount === 'number' && wordcount > 0) {
    return Math.max(1, wordcount / 300);
  }
  return 1; // Base weight
};

/**
 * Hook to group tasks by levels and weeks.
 */
export const useGroupedTasks = (tasks: Task[], levels: Level[], targetWeeks: number | null) => useMemo(() => {
  const effectiveLevels = levels.length > 0 ? levels : [{ id: 1, title: 'Module Content' }];
  const now = new Date();

  const isPacingDisabled = targetWeeks === null;

  const startDates = tasks
    .filter((t) => t.submission?.createdAt)
    .map((t) => new Date(t.submission!.createdAt!).getTime());
  const startDate = startDates.length > 0 ? new Date(Math.min(...startDates)) : now;

  // Calculate elapsed weeks (0-indexed)
  const daysElapsed = Math.max(0, (now.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
  const elapsedWeeks = Math.floor(daysElapsed / 7);

  const effectiveWeeks = isPacingDisabled ? 1 : Math.max(elapsedWeeks + 1, targetWeeks);
  const remainingWeeks = isPacingDisabled ? 1 : Math.max(1, targetWeeks - elapsedWeeks);

  const weekRanges: Array<{ weekNum: number; startDate: Date; endDate: Date }> = [];
  if (!isPacingDisabled) {
    for (let i = 0; i < effectiveWeeks; i++) {
      const wStart = new Date(startDate);
      wStart.setDate(wStart.getDate() + i * 7);
      const wEnd = new Date(wStart);
      wEnd.setDate(wEnd.getDate() + 7);
      weekRanges.push({ weekNum: i + 1, startDate: wStart, endDate: wEnd });
    }
  }

  const taskWeekMap = new Map<string, number>();

  if (isPacingDisabled) {
    tasks.forEach((t) => taskWeekMap.set(t.id, 1));
  } else {
    // 1. Assign completed tasks based on their modifiedAt date
    const completedTasks = tasks.filter((t) => t.submission?.state === 'marked_as_done');
    completedTasks.forEach((task) => {
      const modifiedAt = task.submission?.modifiedAt ? new Date(task.submission.modifiedAt) : now;
      const daysSinceStart = Math.max(0, (modifiedAt.getTime() - startDate.getTime()) / (1000 * 60 * 60 * 24));
      const weekNum = Math.min(effectiveWeeks, Math.floor(daysSinceStart / 7) + 1);
      taskWeekMap.set(task.id, weekNum);
    });

    // 2. Assign remaining tasks distributed by weight over remaining weeks
    const remainingTasks = tasks.filter((t) => t.submission?.state !== 'marked_as_done')
      .sort((a, b) => {
        const levelDiff = (a.level || 1) - (b.level || 1);
        if (levelDiff !== 0) return levelDiff;
        return (a.sortIndex || 0) - (b.sortIndex || 0);
      });

    const totalWeight = remainingTasks.reduce((sum, t) => sum + getTaskWeight(t), 0);
    const weightPerWeek = totalWeight / remainingWeeks;

    let currentWeight = 0;
    let currentRelativeWeek = 0;

    remainingTasks.forEach((task) => {
      const weight = getTaskWeight(task);

      // If previous tasks already filled the current week's capacity, move to next week
      while (currentWeight >= (currentRelativeWeek + 1) * weightPerWeek && currentRelativeWeek < remainingWeeks - 1) {
        currentRelativeWeek += 1;
      }

      currentWeight += weight;

      const absoluteWeekNum = elapsedWeeks + 1 + currentRelativeWeek;
      taskWeekMap.set(task.id, absoluteWeekNum);
    });
  }

  const levelGroups = effectiveLevels.map((level) => {
    const levelTasks = tasks
      .filter((t) => (t.level || 1) === level.id)
      .sort((a, b) => (a.sortIndex || 0) - (b.sortIndex || 0));

    const weekMap = new Map<number, Array<Task & { targetWeek?: number }>>();

    levelTasks.forEach((task) => {
      const weekNum = taskWeekMap.get(task.id) || 1;
      const enrichedTask = { ...task, targetWeek: weekNum };

      if (!weekMap.has(weekNum)) {
        weekMap.set(weekNum, []);
      }
      weekMap.get(weekNum)!.push(enrichedTask);
    });

    const weeks = Array.from(weekMap.entries())
      .sort((a, b) => a[0] - b[0])
      .map(([weekNum, weekTasks]) => ({
        weekNum,
        tasks: weekTasks,
        isMilestoneWeek: weekTasks.some((t) => t.type === 'event'),
        dateRange: weekRanges.find((r) => r.weekNum === weekNum),
      }));

    return { level, weeks };
  });

  return {
    levelGroups, weekRanges, taskWeekMap, totalWeeks: effectiveWeeks, isPacingDisabled, startDate,
  };
}, [tasks, levels, targetWeeks]);
